/**
 * Utilities for building and managing deal channel tracking
 * for recommendation algorithm
 */

// ============================================================================
// Types
// ============================================================================

/**
 * View types representing the highest-level UI layer/screen
 */
export type ClientView =
  | "search_view"
  | "shop_view"
  | "deal_detail"
  | "cart_view"
  | "checkout_view"
  | "profile_view"
  | "subsidy_view"
  | "budget_view"
  | "ad_view";

/**
 * Section types within tabs
 */
export type ClientViewSection =
  | "best_seller_deals_section"
  | "category_deals_section"
  | "deal_detail_section"
  | "similar_deals_section"
  | "top_picks_section"
  | "subsidy_deals_section"
  | "budget_deals_section"
  | "recommended_section"
  | null;

/**
 * Channel data structure
 */
export interface ChannelData {
  c_view: ClientView | null;
  c_tab: string | null;
  c_section: ClientViewSection;
  c_query: string | null;
  c_source_deal_id: string | null;
}

// ============================================================================
// Channel Builder Functions
// ============================================================================

/**
 * Build channel JSON string for API requests
 */
export function buildChannel(data: Partial<ChannelData>): string {
  const channel: ChannelData = {
    c_view: data.c_view || null,
    c_tab: data.c_tab || null,
    c_section: data.c_section || null,
    c_query: data.c_query || null,
    c_source_deal_id: data.c_source_deal_id || null,
  };

  return JSON.stringify(channel);
}

/**
 * Build channel for shop view with tab
 */
export function buildShopChannel(tab: string, section?: ClientViewSection): string {
  return buildChannel({
    c_view: "shop_view",
    c_tab: tab,
    c_section: section || null,
  });
}

/**
 * Build channel for search view
 */
export function buildSearchChannel(query: string): string {
  return buildChannel({
    c_view: "search_view",
    c_query: query,
  });
}

/**
 * Build channel for deal detail view (similar deals)
 */
export function buildDealDetailChannel(): string {
  return buildChannel({
    c_view: "deal_detail",
    c_section: "deal_detail_section",
  });
}

/**
 * Build channel for deal detail view (similar deals)
 */
export function buildSimilarDealChannel(sourceDealId: string): string {
  return buildChannel({
    c_view: "deal_detail",
    c_source_deal_id: sourceDealId,
    c_section: "similar_deals_section",
  });
}

/**
 * Build channel for cart view
 */
export function buildCartChannel(): string {
  return buildChannel({
    c_view: "cart_view",
  });
}

/**
 * Build channel for checkout view
 */
export function buildCheckoutChannel(): string {
  return buildChannel({
    c_view: "checkout_view",
  });
}

/**
 * Build channel for subsidy program view
 * @param categoryId - The subsidy category ID (when on dedicated page)
 * @param parentTabId - The parent tab ID from deal-feeds page (when in trending section)
 */
export function buildSubsidyChannel(categoryId?: string, parentTabId?: string): string {
  if (parentTabId) {
    // When shown in the trending section of deal-feeds
    return buildChannel({
      c_view: "shop_view",
      c_tab: parentTabId,
      c_section: "subsidy_deals_section",
    });
  }
  // When shown on the dedicated subsidy program page
  return buildChannel({
    c_view: "subsidy_view",
    c_tab: categoryId || null,
  });
}

/**
 * Build channel for budget deals view
 * @param parentTabId - The parent tab ID from deal-feeds page (when in trending section)
 */
export function buildBudgetChannel(categoryId?: string, parentTabId?: string): string {
  if (parentTabId) {
    // When shown in the trending section of deal-feeds
    return buildChannel({
      c_view: "shop_view",
      c_tab: parentTabId,
      c_section: "budget_deals_section",
    });
  }
  // When shown on the dedicated budget deals page
  return buildChannel({
    c_view: "budget_view",
    c_tab: categoryId || null,
  });
}

/**
 * Build channel for ad landing page
 */
export function buildAdChannel(): string {
  return buildChannel({
    c_view: "ad_view",
  });
}

/**
 * Build channel for profile/recently viewed
 */
export function buildProfileChannel(): string {
  return buildChannel({
    c_view: "profile_view",
  });
}
